import { supabase } from '../lib/supabase';
import { twilioService } from './twilioService';
import auctionEmailService from './auctionEmailService';

export const marketplaceService = {
  // Create new marketplace listing
  async createListing(listingData) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_listings')
        ?.insert([listingData])
        ?.select(`
          *,
          seller:user_profiles!seller_id(id, full_name, avatar_url),
          collectible:collectibles(id, name, collectible_type, condition, primary_image_url)
        `)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating listing:', error);
      return { data: null, error: error?.message || 'Failed to create listing' };
    }
  },

  // Get all active marketplace listings
  async getActiveListings(filters = {}) {
    try {
      let query = supabase
        ?.from('marketplace_listings')
        ?.select(`
          *,
          seller:user_profiles!seller_id(id, full_name, avatar_url, membership_tier),
          collectible:collectibles(id, name, collectible_type, condition, primary_image_url, brand, year)
        `)
        ?.eq('status', 'active')
        ?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.collectibleType) {
        query = query?.eq('collectible.collectible_type', filters?.collectibleType);
      }
      if (filters?.minPrice) {
        query = query?.gte('asking_price', filters?.minPrice);
      }
      if (filters?.maxPrice) {
        query = query?.lte('asking_price', filters?.maxPrice);
      }
      if (filters?.condition) {
        query = query?.eq('condition', filters?.condition);
      }
      if (filters?.search) {
        query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching listings:', error);
      return { data: [], error: error?.message || 'Failed to fetch listings' };
    }
  },

  // Get user's own listings
  async getUserListings(userId, status = null) {
    try {
      let query = supabase
        ?.from('marketplace_listings')
        ?.select(`
          *,
          collectible:collectibles(id, name, collectible_type, condition, primary_image_url),
          offers:trade_offers(id, buyer_id, offer_amount, status, created_at)
        `)
        ?.eq('seller_id', userId)
        ?.order('created_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching user listings:', error);
      return { data: [], error: error?.message || 'Failed to fetch user listings' };
    }
  },

  // Get single listing details
  async getListingById(listingId) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_listings')
        ?.select(`
          *,
          seller:user_profiles!seller_id(id, full_name, avatar_url, membership_tier),
          collectible:collectibles(id, name, description, collectible_type, condition, brand, model, year, primary_image_url, images:collectible_images(image_url, caption))
        `)
        ?.eq('id', listingId)
        ?.single();

      if (error) throw error;

      // Increment view count
      await supabase
        ?.from('marketplace_listings')
        ?.update({ view_count: (data?.view_count || 0) + 1 })
        ?.eq('id', listingId);

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching listing:', error);
      return { data: null, error: error?.message || 'Failed to fetch listing' };
    }
  },

  // Update listing
  async updateListing(listingId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_listings')
        ?.update(updates)
        ?.eq('id', listingId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating listing:', error);
      return { data: null, error: error?.message || 'Failed to update listing' };
    }
  },

  // Delete/Cancel listing
  async cancelListing(listingId) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_listings')
        ?.update({ status: 'cancelled' })
        ?.eq('id', listingId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error cancelling listing:', error);
      return { data: null, error: error?.message || 'Failed to cancel listing' };
    }
  },

  // Create trade offer
  async createOffer(offerData) {
    try {
      const { data, error } = await supabase
        ?.from('trade_offers')
        ?.insert([offerData])
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name, avatar_url),
          listing:marketplace_listings(id, title, asking_price, seller_id)
        `)
        ?.single();

      if (error) throw error;

      // Send SMS notification to seller
      if (data?.listing?.seller_id) {
        await twilioService?.sendMarketplaceNotification(
          data?.listing?.seller_id,
          'offer_received',
          {
            amount: data?.offer_amount,
            listingTitle: data?.listing?.title,
            buyerName: data?.buyer?.full_name
          }
        );
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating offer:', error);
      return { data: null, error: error?.message || 'Failed to create offer' };
    }
  },

  // Get offers for a listing (seller view)
  async getListingOffers(listingId) {
    try {
      const { data, error } = await supabase
        ?.from('trade_offers')
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name, avatar_url, membership_tier)
        `)
        ?.eq('listing_id', listingId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching listing offers:', error);
      return { data: [], error: error?.message || 'Failed to fetch offers' };
    }
  },

  // Get user's offers (buyer view)
  async getUserOffers(userId) {
    try {
      const { data, error } = await supabase
        ?.from('trade_offers')
        ?.select(`
          *,
          listing:marketplace_listings(id, title, asking_price, featured_image_url, status, seller_id),
          seller:user_profiles!seller_id(id, full_name, avatar_url)
        `)
        ?.eq('buyer_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching user offers:', error);
      return { data: [], error: error?.message || 'Failed to fetch user offers' };
    }
  },

  // Update offer status (accept/reject/counter)
  async updateOffer(offerId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('trade_offers')
        ?.update({ ...updates, responded_at: new Date()?.toISOString() })
        ?.eq('id', offerId)
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name),
          listing:marketplace_listings(id, title)
        `)
        ?.single();

      if (error) throw error;

      // Send SMS notification to buyer based on status
      if (data?.buyer_id) {
        let notificationType = null;
        let metadata = {
          listingTitle: data?.listing?.title,
          amount: data?.offer_amount
        };

        if (updates?.status === 'accepted') {
          notificationType = 'offer_accepted';
        } else if (updates?.status === 'rejected') {
          notificationType = 'offer_rejected';
        } else if (updates?.status === 'countered') {
          notificationType = 'offer_countered';
          metadata.counterAmount = updates?.counter_offer_amount;
        }

        if (notificationType) {
          await twilioService?.sendMarketplaceNotification(
            data?.buyer_id,
            notificationType,
            metadata
          );
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating offer:', error);
      return { data: null, error: error?.message || 'Failed to update offer' };
    }
  },

  // Create escrow transaction
  async createEscrowTransaction(transactionData) {
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.insert([transactionData])
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name, email),
          seller:user_profiles!seller_id(id, full_name, email),
          listing:marketplace_listings(id, title, collectible_id)
        `)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating escrow transaction:', error);
      return { data: null, error: error?.message || 'Failed to create escrow transaction' };
    }
  },

  // Get escrow transaction by payment intent
  async getEscrowByPaymentIntent(paymentIntentId) {
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name, email),
          seller:user_profiles!seller_id(id, full_name, email),
          listing:marketplace_listings(id, title, collectible_id),
          offer:trade_offers(id, offer_amount)
        `)
        ?.eq('payment_intent_id', paymentIntentId)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching escrow transaction:', error);
      return { data: null, error: error?.message || 'Failed to fetch escrow transaction' };
    }
  },

  // Update escrow transaction
  async updateEscrowTransaction(transactionId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.update(updates)
        ?.eq('id', transactionId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating escrow transaction:', error);
      return { data: null, error: error?.message || 'Failed to update escrow transaction' };
    }
  },

  // Confirm transaction (buyer or seller)
  async confirmTransaction(transactionId, userRole) {
    try {
      const updates = userRole === 'buyer' 
        ? { buyer_confirmed: true, buyer_confirmed_at: new Date()?.toISOString() }
        : { seller_confirmed: true, seller_confirmed_at: new Date()?.toISOString() };

      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.update(updates)
        ?.eq('id', transactionId)
        ?.select()
        ?.single();

      if (error) throw error;

      // If both confirmed, release funds
      if (data?.buyer_confirmed && data?.seller_confirmed) {
        await this.releaseEscrow(transactionId);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error confirming transaction:', error);
      return { data: null, error: error?.message || 'Failed to confirm transaction' };
    }
  },

  // Confirm escrow payment (buyer confirms)
  async confirmEscrowPayment(transactionId, role) {
    try {
      const updateField = role === 'buyer' ? 'buyer_confirmed' : 'seller_confirmed';
      const timestampField = role === 'buyer' ? 'buyer_confirmed_at' : 'seller_confirmed_at';

      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.update({
          [updateField]: true,
          [timestampField]: new Date()?.toISOString()
        })
        ?.eq('id', transactionId)
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name),
          seller:user_profiles!seller_id(id, full_name),
          listing:marketplace_listings(id, title)
        `)
        ?.single();

      if (error) throw error;

      // Send SMS notification when payment is confirmed
      if (role === 'buyer' && data?.seller_id) {
        await twilioService?.sendMarketplaceNotification(
          data?.seller_id,
          'escrow_payment_received',
          {
            amount: data?.amount,
            listingTitle: data?.listing?.title,
            buyerName: data?.buyer?.full_name
          }
        );
      }

      // Check if both parties confirmed, release funds
      if (data?.buyer_confirmed && data?.seller_confirmed) {
        await this.releaseEscrowFunds(transactionId);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error confirming escrow payment:', error);
      return { data: null, error: error?.message || 'Failed to confirm payment' };
    }
  },

  // Release escrow funds
  async releaseEscrowFunds(transactionId) {
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.update({
          status: 'released',
          released_at: new Date()?.toISOString()
        })
        ?.eq('id', transactionId)
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name),
          seller:user_profiles!seller_id(id, full_name),
          listing:marketplace_listings(id, title)
        `)
        ?.single();

      if (error) throw error;

      // Send SMS notifications to both parties
      const notificationMetadata = {
        amount: data?.amount,
        listingTitle: data?.listing?.title
      };

      if (data?.seller_id) {
        await twilioService?.sendMarketplaceNotification(
          data?.seller_id,
          'escrow_funds_released',
          notificationMetadata
        );
      }

      if (data?.buyer_id) {
        await twilioService?.sendMarketplaceNotification(
          data?.buyer_id,
          'transaction_completed',
          notificationMetadata
        );
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error releasing escrow funds:', error);
      return { data: null, error: error?.message || 'Failed to release funds' };
    }
  },

  // Open dispute
  async openDispute(transactionId, disputeReason, disputedBy) {
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.update({
          status: 'disputed',
          metadata: {
            dispute_reason: disputeReason,
            disputed_by: disputedBy,
            disputed_at: new Date()?.toISOString()
          }
        })
        ?.eq('id', transactionId)
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name),
          seller:user_profiles!seller_id(id, full_name),
          listing:marketplace_listings(id, title)
        `)
        ?.single();

      if (error) throw error;

      // Send SMS notification to the other party
      const otherPartyId = disputedBy === data?.buyer_id ? data?.seller_id : data?.buyer_id;
      if (otherPartyId) {
        await twilioService?.sendMarketplaceNotification(
          otherPartyId,
          'dispute_opened',
          {
            listingTitle: data?.listing?.title,
            amount: data?.amount
          }
        );
      }

      // Send dispute opened emails to both buyer and seller
      auctionEmailService?.sendDisputeOpenedEmail(data?.buyer_id, data?.seller_id, {
        id: transactionId,
        itemTitle: data?.listing?.title || 'Item',
        amount: data?.amount
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error opening dispute:', error);
      return { data: null, error: error?.message || 'Failed to open dispute' };
    }
  },

  // Resolve dispute
  async resolveDispute(transactionId, resolution, resolvedBy) {
    try {
      const { data: transaction } = await supabase
        ?.from('escrow_transactions')
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name),
          seller:user_profiles!seller_id(id, full_name),
          listing:marketplace_listings(id, title)
        `)
        ?.eq('id', transactionId)
        ?.single();

      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.update({
          status: resolution === 'refund' ? 'refunded' : 'released',
          metadata: {
            ...transaction?.metadata,
            dispute_resolution: resolution,
            resolved_by: resolvedBy,
            resolved_at: new Date()?.toISOString()
          },
          refunded_at: resolution === 'refund' ? new Date()?.toISOString() : null,
          released_at: resolution === 'release' ? new Date()?.toISOString() : null
        })
        ?.eq('id', transactionId)
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name),
          seller:user_profiles!seller_id(id, full_name),
          listing:marketplace_listings(id, title)
        `)
        ?.single();

      if (error) throw error;

      // Send SMS notifications to both parties
      const notificationMetadata = {
        listingTitle: data?.listing?.title,
        amount: data?.amount,
        resolution: resolution
      };

      if (data?.buyer_id) {
        await twilioService?.sendMarketplaceNotification(
          data?.buyer_id,
          'dispute_resolved',
          notificationMetadata
        );
      }

      if (data?.seller_id) {
        await twilioService?.sendMarketplaceNotification(
          data?.seller_id,
          'dispute_resolved',
          notificationMetadata
        );
      }

      // Send dispute resolved emails to both parties
      auctionEmailService?.sendDisputeResolvedEmail(data?.buyer_id, data?.seller_id, {
        id: transactionId,
        itemTitle: data?.listing?.title || 'Item',
        amount: data?.amount
      }, resolution);

      return { data, error: null };
    } catch (error) {
      console.error('Error resolving dispute:', error);
      return { data: null, error: error?.message || 'Failed to resolve dispute' };
    }
  },

  // Release escrow
  async releaseEscrow(transactionId) {
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.update({ 
          status: 'released', 
          released_at: new Date()?.toISOString() 
        })
        ?.eq('id', transactionId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Create trade history record
      if (data) {
        await supabase
          ?.from('trade_history')
          ?.insert([{
            transaction_id: data?.id,
            listing_id: data?.listing_id,
            collectible_id: data?.listing?.collectible_id,
            buyer_id: data?.buyer_id,
            seller_id: data?.seller_id,
            final_price: data?.amount,
            currency: data?.currency
          }]);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error releasing escrow:', error);
      return { data: null, error: error?.message || 'Failed to release escrow' };
    }
  },

  // Get user's active transactions
  async getUserTransactions(userId) {
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name, avatar_url),
          seller:user_profiles!seller_id(id, full_name, avatar_url),
          listing:marketplace_listings(id, title, featured_image_url)
        `)
        ?.or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      return { data: [], error: error?.message || 'Failed to fetch transactions' };
    }
  },

  // Get trade history
  async getTradeHistory(userId) {
    try {
      const { data, error } = await supabase
        ?.from('trade_history')
        ?.select(`
          *,
          buyer:user_profiles!buyer_id(id, full_name, avatar_url),
          seller:user_profiles!seller_id(id, full_name, avatar_url),
          listing:marketplace_listings(id, title, featured_image_url),
          collectible:collectibles(id, name, collectible_type, primary_image_url)
        `)
        ?.or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
        ?.order('completed_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching trade history:', error);
      return { data: [], error: error?.message || 'Failed to fetch trade history' };
    }
  },

  // Get user reputation
  async getUserReputation(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_reputation')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      
      // Return default reputation if not found
      return { 
        data: data || {
          user_id: userId,
          total_sales: 0,
          total_purchases: 0,
          seller_rating: 0,
          buyer_rating: 0,
          successful_trades: 0
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching user reputation:', error);
      return { data: null, error: error?.message || 'Failed to fetch user reputation' };
    }
  },

  // Submit rating and review
  async submitReview(tradeHistoryId, reviewData) {
    try {
      const { data, error } = await supabase
        ?.from('trade_history')
        ?.update(reviewData)
        ?.eq('id', tradeHistoryId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error submitting review:', error);
      return { data: null, error: error?.message || 'Failed to submit review' };
    }
  },

  // ==================== AUCTION FUNCTIONS ====================

  // Create new auction
  async createAuction(auctionData) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_auctions')
        ?.insert([auctionData])
        ?.select(`
          *,
          seller:user_profiles!seller_id(id, full_name, avatar_url),
          collectible:collectibles(id, name, collectible_type, condition, primary_image_url),
          category:auction_categories(id, name, collectible_type)
        `)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating auction:', error);
      return { data: null, error: error?.message || 'Failed to create auction' };
    }
  },

  // Get all active auctions
  async getActiveAuctions(filters = {}) {
    try {
      let query = supabase
        ?.from('marketplace_auctions')
        ?.select(`
          *,
          seller:user_profiles!seller_id(id, full_name, avatar_url, membership_tier),
          collectible:collectibles(id, name, collectible_type, condition, primary_image_url, brand, year),
          category:auction_categories(id, name, collectible_type),
          bids:auction_bids(id, bidder_id, bid_amount, created_at)
        `)
        ?.in('status', ['scheduled', 'active'])
        ?.order('start_date', { ascending: true });

      // Apply filters
      if (filters?.auctionType) {
        query = query?.eq('auction_type', filters?.auctionType);
      }
      if (filters?.collectibleType) {
        query = query?.eq('collectible.collectible_type', filters?.collectibleType);
      }
      if (filters?.categoryId) {
        query = query?.eq('category_id', filters?.categoryId);
      }
      if (filters?.search) {
        query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching auctions:', error);
      return { data: [], error: error?.message || 'Failed to fetch auctions' };
    }
  },

  // Get user's own auctions
  async getUserAuctions(userId, status = null) {
    try {
      let query = supabase
        ?.from('marketplace_auctions')
        ?.select(`
          *,
          collectible:collectibles(id, name, collectible_type, condition, primary_image_url),
          category:auction_categories(id, name),
          bids:auction_bids(id, bidder_id, bid_amount, is_winning, created_at)
        `)
        ?.eq('seller_id', userId)
        ?.order('created_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching user auctions:', error);
      return { data: [], error: error?.message || 'Failed to fetch user auctions' };
    }
  },

  // Get single auction details
  async getAuctionById(auctionId) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_auctions')
        ?.select(`
          *,
          seller:user_profiles!seller_id(id, full_name, avatar_url, membership_tier),
          collectible:collectibles(id, name, description, collectible_type, condition, brand, model, year, primary_image_url, images:collectible_images(image_url, caption)),
          category:auction_categories(id, name, collectible_type, description),
          bids:auction_bids(
            id, 
            bidder_id, 
            bid_amount, 
            is_winning, 
            created_at,
            bidder:user_profiles!bidder_id(id, full_name, avatar_url)
          )
        `)
        ?.eq('id', auctionId)
        ?.single();

      if (error) throw error;

      // Increment view count
      await supabase
        ?.from('marketplace_auctions')
        ?.update({ view_count: (data?.view_count || 0) + 1 })
        ?.eq('id', auctionId);

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching auction:', error);
      return { data: null, error: error?.message || 'Failed to fetch auction' };
    }
  },

  // Update auction
  async updateAuction(auctionId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_auctions')
        ?.update(updates)
        ?.eq('id', auctionId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating auction:', error);
      return { data: null, error: error?.message || 'Failed to update auction' };
    }
  },

  // Cancel auction
  async cancelAuction(auctionId) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_auctions')
        ?.update({ status: 'cancelled' })
        ?.eq('id', auctionId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error cancelling auction:', error);
      return { data: null, error: error?.message || 'Failed to cancel auction' };
    }
  },

  // Place bid on auction
  async placeBid(bidData) {
    try {
      // First check if auction is active and bid is valid
      const { data: auction } = await supabase
        ?.from('marketplace_auctions')
        ?.select('id, current_bid, bid_increment, status, seller_id')
        ?.eq('id', bidData?.auction_id)
        ?.single();

      if (!auction || auction?.status !== 'active') {
        return { data: null, error: 'Auction is not active' };
      }

      if (auction?.seller_id === bidData?.bidder_id) {
        return { data: null, error: 'Cannot bid on your own auction' };
      }

      const minBid = parseFloat(auction?.current_bid || 0) + parseFloat(auction?.bid_increment || 0);
      if (parseFloat(bidData?.bid_amount) < minBid) {
        return { data: null, error: `Minimum bid is $${minBid?.toFixed(2)}` };
      }

      // Place bid
      const { data, error } = await supabase
        ?.from('auction_bids')
        ?.insert([bidData])
        ?.select(`
          *,
          auction:marketplace_auctions(id, title, current_bid, seller_id),
          bidder:user_profiles!bidder_id(id, full_name, avatar_url)
        `)
        ?.single();

      if (error) throw error;

      // Send SMS notification to seller
      if (data?.auction?.seller_id) {
        await twilioService?.sendMarketplaceNotification(
          data?.auction?.seller_id,
          'auction_bid',
          {
            amount: data?.bid_amount,
            auctionTitle: data?.auction?.title,
            bidderName: data?.bidder?.full_name
          }
        );
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error placing bid:', error);
      // Handle specific database trigger error for self-bidding
      if (error?.message?.includes('Sellers cannot bid on their own auctions')) {
        return { data: null, error: 'Cannot bid on your own auction' };
      }
      return { data: null, error: error?.message || 'Failed to place bid' };
    }
  },

  // Get auction bids
  async getAuctionBids(auctionId) {
    try {
      const { data, error } = await supabase
        ?.from('auction_bids')
        ?.select(`
          *,
          bidder:user_profiles!bidder_id(id, full_name, avatar_url, membership_tier)
        `)
        ?.eq('auction_id', auctionId)
        ?.order('bid_amount', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching auction bids:', error);
      return { data: [], error: error?.message || 'Failed to fetch bids' };
    }
  },

  // Get user's bids
  async getUserBids(userId) {
    try {
      const { data, error } = await supabase
        ?.from('auction_bids')
        ?.select(`
          *,
          auction:marketplace_auctions(
            id, 
            title, 
            current_bid, 
            status, 
            end_date, 
            featured_image_url,
            seller:user_profiles!seller_id(id, full_name)
          )
        `)
        ?.eq('bidder_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching user bids:', error);
      return { data: [], error: error?.message || 'Failed to fetch user bids' };
    }
  },

  // Get auction categories
  async getAuctionCategories() {
    try {
      const { data, error } = await supabase
        ?.from('auction_categories')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching auction categories:', error);
      return { data: [], error: error?.message || 'Failed to fetch categories' };
    }
  },

  // Get premiere auctions by category
  async getPremiereAuctions(categoryId = null) {
    try {
      let query = supabase
        ?.from('marketplace_auctions')
        ?.select(`
          *,
          seller:user_profiles!seller_id(id, full_name, avatar_url),
          collectible:collectibles(id, name, collectible_type, condition, primary_image_url),
          category:auction_categories(id, name, collectible_type),
          bids:auction_bids(id, bid_amount)
        `)
        ?.eq('auction_type', 'premiere')
        ?.in('status', ['scheduled', 'active'])
        ?.order('start_date', { ascending: true });

      if (categoryId) {
        query = query?.eq('category_id', categoryId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching premiere auctions:', error);
      return { data: [], error: error?.message || 'Failed to fetch premiere auctions' };
    }
  }
};

export default marketplaceService;
